/**
 * THE Yes/No confirmation (unit convention §3): one modal, one question, two
 * answers, tokens only.
 *
 * Decision D4 brought it back for Resign AND Abort on the web. The M1a lane had
 * a hold-to-confirm ring instead (design 04h), which the review flagged twice:
 * Abort never got one at all, so a single mis-tap ended a rated game, and a
 * 600 ms hold is an invisible affordance next to a question the old screen asks
 * out loud. The copy is the old `BottomControlsComponent`'s, verbatim
 * (index.tsx L159 and L178) — the call sites pass it.
 *
 * It is an RN `Modal` (react-native-web renders it as a fixed overlay), so it
 * cannot be clipped by the Rail's `overflow: hidden` the way an absolutely
 * positioned card would.
 */
import React from 'react';
import {Modal, StyleSheet, View} from 'react-native';

import {RADIUS, SPACE} from '../theme/tokens';
import {useV2Theme} from '../theme/useV2Theme';
import Button from './Button';
import Txt from './Txt';

/** the old modal is a narrow sheet, not a full-width card (styles L96-108) */
const WIDTH = 320;

export type ConfirmDialogProps = {
  visible: boolean;
  /** the question, e.g. "Are you sure you want to resign?" */
  title: string;
  /** Yes */
  onConfirm: () => void;
  /** No, and the backdrop / Esc */
  onCancel: () => void;
  testID?: string;
};

export default React.memo(function ConfirmDialog({
  visible,
  title,
  onConfirm,
  onCancel,
  testID,
}: ConfirmDialogProps) {
  const tokens = useV2Theme(s => s.tokens);
  if (!visible) {
    return null;
  }
  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}>
      <View
        testID={testID}
        style={[styles.scrim, {backgroundColor: tokens.overlayScrim}]}>
        <View
          style={[
            styles.sheet,
            {
              backgroundColor: tokens.surface,
              borderColor: tokens.cardline,
              boxShadow: tokens.sh2,
            } as any,
          ]}>
          <Txt variant="cardTitle" tone="ink">
            {title}
          </Txt>
          <View style={styles.row}>
            <Button
              label="No"
              accessibilityLabel="No"
              variant="outline"
              size="md"
              flex={1}
              onPress={onCancel}
            />
            <Button
              label="Yes"
              accessibilityLabel="Yes"
              variant="danger"
              size="md"
              flex={1}
              onPress={onConfirm}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
});

const styles = StyleSheet.create({
  scrim: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheet: {
    width: WIDTH,
    maxWidth: '90%',
    borderRadius: RADIUS.card,
    borderWidth: 1,
    padding: SPACE.xl,
    gap: SPACE.lg,
  },
  row: {
    flexDirection: 'row',
    gap: SPACE.md,
  },
});
